<template>
  <div class="layout-wrapper">
    <header v-if="!hideNav" class="mobile-topbar">
      <button type="button" class="mobile-menu-btn" aria-label="打开导航" @click="openMobileNav">
        <MenuOutlined />
      </button>
      <a class="mobile-logo" href="/" @click="closeMobileNav">
        <img src="../assets/logo.png" alt="logo" class="mobile-logo-img" />
      </a>
      <div class="mobile-title">{{ currentRouteTitle }}</div>
    </header>
    <div
      v-if="!hideNav"
      class="mobile-nav-mask"
      :class="{ show: isMobileNavOpen }"
      @click="closeMobileNav"
    />

    <!-- 左侧导航 -->
    <aside
      v-if="!hideNav"
      class="sidebar"
      :class="{ collapsed: sidebarCollapsed, 'mobile-open': isMobileNavOpen }"
    >
      <div class="sidebar-header">
        <a
          class="logo"
          :href="sidebarCollapsed && !isMobileNavOpen ? undefined : '/'"
          @click="handleLogoClick"
        >
          <img v-if="showSidebarText" src="../assets/logo.png" alt="logo" class="logo-full" />
          <img v-else src="../assets/logo-icon.png" alt="logo" class="logo-full" />
        </a>
        <div class="collapse-btn desktop-collapse-btn" @click="toggleSidebar">
          <MenuUnfoldOutlined v-if="sidebarCollapsed" />
          <MenuFoldOutlined v-else />
        </div>
        <button
          type="button"
          class="collapse-btn mobile-close-btn"
          aria-label="关闭导航"
          @click="closeMobileNav"
        >
          <CloseOutlined />
        </button>
      </div>

      <nav class="sidebar-nav">
        <template v-for="link in menus" :key="link.path">
          <!-- 有子菜单 -->
          <div
            v-if="link.children && link.meta.showChildren && link.path !== '/'"
            class="nav-group"
          >
            <div
              class="nav-item nav-parent"
              :class="{ active: isParentActive(link), expanded: expandedMenus.includes(link.path) }"
              @click="toggleMenu(link.path)"
            >
              <component
                :is="navIconComponent(link.meta.icon || defaultIcons[link.meta.title] || 'folder')"
                class="nav-icon"
              />
              <span v-if="showSidebarText" class="nav-text">{{ t(link.meta.title) }}</span>
              <DownOutlined
                v-if="showSidebarText"
                class="nav-arrow"
                :class="{ rotated: expandedMenus.includes(link.path) }"
              />
            </div>
            <div
              v-if="showSidebarText"
              class="nav-submenu"
              :class="{ show: expandedMenus.includes(link.path) }"
            >
              <div class="nav-submenu-inner" :inert="!expandedMenus.includes(link.path)">
                <div class="nav-submenu-content">
                  <router-link
                    v-for="sublink in link.children"
                    :key="sublink.name + sublink.path"
                    :to="sublink.path"
                    class="nav-item nav-child"
                    @click="closeMobileNav"
                  >
                    <component
                      :is="navIconComponent(sublink.meta.icon || 'minus')"
                      class="nav-icon"
                    />
                    <span class="nav-text">{{ t(sublink.meta.title) }}</span>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
          <!-- 无子菜单 -->
          <router-link
            v-else
            :key="link.name"
            :to="link.path"
            class="nav-item"
            :class="{ active: highlightNav(link.path) }"
            @click="closeMobileNav"
          >
            <component
              :is="navIconComponent(link.meta.icon || defaultIcons[link.meta.title] || 'appstore')"
              class="nav-icon"
            />
            <span v-if="showSidebarText" class="nav-text">{{ t(link.meta.title) }}</span>
          </router-link>
        </template>
        <a class="nav-item nav-logout" @click="logout">
          <LogoutOutlined class="nav-icon" />
          <span v-if="showSidebarText" class="nav-text">退出登录</span>
        </a>
      </nav>
    </aside>

    <!-- 主内容区 -->
    <main
      class="main-area"
      :class="{
        'sidebar-collapsed': sidebarCollapsed,
        'no-sidebar': hideNav,
        'has-mobile-nav': !hideNav
      }"
    >
      <router-view />
    </main>
    <ScrollToTop />
  </div>
</template>

<script setup>
import {
  AccountBookOutlined,
  AppstoreOutlined,
  BankOutlined,
  CompassOutlined,
  CloseOutlined,
  CreditCardOutlined,
  DatabaseOutlined,
  DownOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FileMarkdownOutlined,
  FileTextOutlined,
  FolderOutlined,
  HomeOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  MinusOutlined,
  MoneyCollectOutlined,
  ProfileOutlined,
  ProjectOutlined,
  PushpinOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  SwapOutlined,
  TeamOutlined
} from "@antdv-next/icons";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useUserStore } from "@/store/modules/user.js";

import ScrollToTop from "@/components/ScrollToTop/index.vue";
import { asyncRouterMap } from "@/config/router.config.js";
import { canAccessActionRoute } from "@/utils/actionRoutePermission.mjs";

const navIconMap = {
  "account-book": AccountBookOutlined,
  appstore: AppstoreOutlined,
  bank: BankOutlined,
  compass: CompassOutlined,
  "credit-card": CreditCardOutlined,
  database: DatabaseOutlined,
  dollar: DollarOutlined,
  environment: EnvironmentOutlined,
  "file-markdown": FileMarkdownOutlined,
  "file-text": FileTextOutlined,
  folder: FolderOutlined,
  home: HomeOutlined,
  minus: MinusOutlined,
  "money-collect": MoneyCollectOutlined,
  profile: ProfileOutlined,
  project: ProjectOutlined,
  pushpin: PushpinOutlined,
  "safety-certificate": SafetyCertificateOutlined,
  setting: SettingOutlined,
  swap: SwapOutlined,
  team: TeamOutlined
};

defineOptions({
  name: "BlankLayout"
});

const route = useRoute();
const { t } = useI18n();
const userStore = useUserStore();

const sidebarCollapsed = ref(false);
const expandedMenus = ref([]);
const isMobileNavOpen = ref(false);

const defaultIcons = {
  "menu.home": "home",
  "menu.candidates": "team",
  "menu.sales": "dollar",
  "menu.companies": "bank",
  "menu.projects": "project",
  "menu.tasks": "profile",
  "menu.finance": "account-book",
  "menu.admin": "database",
  "menu.settings": "setting"
};

const hideNav = computed(() => route.meta.hideNav);
const menus = computed(() => filterNavMenus(asyncRouterMap));
const currentRouteTitle = computed(() => (route.meta?.title ? t(route.meta.title) : ""));
const showSidebarText = computed(() => !sidebarCollapsed.value || isMobileNavOpen.value);

function normalizeNavPath(path, parentPath = "") {
  if (!path) {
    return parentPath || "/";
  }
  if (path.startsWith("/")) {
    return path;
  }
  const base = parentPath === "/" ? "" : parentPath;
  return `${base}/${path}`.replace(/\/+/g, "/");
}

function makeNavMenuItem(menu, fullPath, children = []) {
  const item = {
    path: fullPath,
    name: menu.name,
    meta: menu.meta || {}
  };
  if (children.length) {
    item.children = children;
  }
  return item;
}

function filterNavMenus(menus) {
  const permissionSet = new Set(userStore.pagePermissions || []);

  function collectMenus(routes, parentPath = "", inGroup = false) {
    let navMenus = [];
    routes.forEach((menu) => {
      const fullPath = normalizeNavPath(menu.path, parentPath);
      const childInGroup = !!menu.meta?.showChildren;
      const children = menu.children ? collectMenus(menu.children, fullPath, childInGroup) : [];
      const canAccess = menu.meta?.requiredActionPermission
        ? canAccessActionRoute(menu.meta, userStore)
        : permissionSet.has(fullPath);

      if (menu.meta?.showInNav && (canAccess || children.length)) {
        navMenus.push(makeNavMenuItem(menu, fullPath, children));
      } else if (inGroup && menu.meta?.title && (canAccess || children.length)) {
        navMenus.push(makeNavMenuItem(menu, fullPath));
      }

      if (menu.path === "/" && menu.children?.length) {
        navMenus = navMenus.concat(children.filter((item) => item.meta?.showInNav));
      }
    });
    return navMenus;
  }

  return collectMenus(menus);
}

function highlightNav(path) {
  if (path === "/") return route.path === "/";
  const currentPath = route.path;
  const isMatch = currentPath === path || currentPath.startsWith(path + "/");
  if (!isMatch) return false;
  // 如果当前路由属于某个有子菜单的父级菜单，则不高亮当前无子菜单的项
  const belongsToParent = menus.value.some((link) => {
    if (link.children && link.meta && link.meta.showChildren && link.path !== path) {
      return link.children.some(
        (child) => currentPath === child.path || currentPath.startsWith(child.path + "/")
      );
    }
    return false;
  });
  return !belongsToParent;
}

function isParentActive(link) {
  if (link.children) {
    return link.children.some((child) => {
      return route.path === child.path || route.path.startsWith(child.path + "/");
    });
  }

  return false;
}

function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value;
  if (sidebarCollapsed.value) {
    expandedMenus.value = [];
  }
}

function openMobileNav() {
  isMobileNavOpen.value = true;
}

function closeMobileNav() {
  isMobileNavOpen.value = false;
}

function handleLogoClick(event) {
  const wasMobileNavOpen = isMobileNavOpen.value;
  closeMobileNav();
  if (!sidebarCollapsed.value || wasMobileNavOpen) return;
  event.preventDefault();
  toggleSidebar();
}

function toggleMenu(path) {
  if (sidebarCollapsed.value && !isMobileNavOpen.value) return;
  const index = expandedMenus.value.indexOf(path);
  if (index > -1) {
    expandedMenus.value.splice(index, 1);
  } else {
    expandedMenus.value.push(path);
  }
}

function navIconComponent(icon) {
  return navIconMap[icon] || AppstoreOutlined;
}

function logout() {
  const shouldLogout = window.confirm("确认退出登录？");
  if (shouldLogout) {
    closeMobileNav();
    userStore.logout();
  }
}

watch(
  () => route.fullPath,
  () => {
    closeMobileNav();
  }
);

onMounted(() => {
  // 默认展开当前激活的父菜单
  menus.value.forEach((link) => {
    if (link.children && isParentActive(link)) {
      expandedMenus.value.push(link.path);
    }
  });
});
</script>

<style scoped lang="less">
@sidebar-width: 240px;
@sidebar-collapsed-width: 72px;
@primary: #1d1d1f;
@secondary: #6e6e73;
@bg-page: #f5f5f7;
@bg-card: #fff;
@border-color: #e5e5e7;

.layout-wrapper {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: @bg-page;
}

.mobile-topbar,
.mobile-nav-mask,
.mobile-close-btn {
  display: none;
}

.mobile-menu-btn,
.mobile-close-btn {
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
}

/* ========== 侧边栏 ========== */
.sidebar {
  width: @sidebar-width;
  background: @bg-card;
  border-right: 1px solid @border-color;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 100;
  transition: width 0.2s ease;

  &.collapsed {
    width: @sidebar-collapsed-width;
  }
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid @border-color;
  min-height: 64px;
}

.logo {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo-full {
  height: 28px;
  width: auto;
}

.logo-mini {
  width: 36px;
  height: 36px;
  background: @primary;
  color: #fff;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
}

.collapse-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  color: @secondary;
  transition: all 0.2s;

  &:hover {
    background: @bg-page;
    color: @primary;
  }
}

.collapse-btn.mobile-close-btn {
  display: none;
}

/* ========== 导航菜单 ========== */
.sidebar-nav {
  flex: 1;
  padding: 12px 8px;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin: 2px 0;
  border-radius: 12px;
  color: @secondary;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  gap: 12px;

  &:hover {
    background: @bg-page;
    color: @secondary;
    text-decoration: none;
  }

  &.active {
    background: @primary;
    color: #fff !important;

    .nav-icon {
      color: #fff !important;
    }

    &:hover {
      background: @primary;
      color: #fff !important;
    }
  }

  &.nav-parent {
    justify-content: space-between;

    &.active {
      background: @primary;
      color: #fff !important;

      .nav-icon,
      .nav-arrow {
        color: #fff !important;
      }

      &:hover {
        background: @primary;
        color: #fff !important;
      }
    }
  }

  &.nav-child {
    padding-left: 48px;
    font-size: 13px;
    color: @secondary;

    &:hover {
      color: @secondary;
    }

    &.router-link-active {
      background: @primary;
      color: #fff !important;
      font-weight: 600;

      .nav-icon {
        color: #fff !important;
      }

      &:hover {
        background: @primary;
        color: #fff !important;

        .nav-icon {
          color: #fff !important;
        }
      }
    }
  }
}

.nav-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.nav-child .nav-icon {
  font-size: 14px;
}

.nav-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-arrow {
  font-size: 12px;
  transition: transform 0.2s;

  &.rotated {
    transform: rotate(180deg);
  }
}

.nav-group {
  margin-bottom: 4px;
}

.nav-logout {
  margin-top: auto;
}

.nav-submenu {
  display: grid;
  grid-template-rows: 0fr;
  overflow: hidden;
  transition: grid-template-rows 0.2s ease;
  background: #fafafa;
  border-radius: 8px;
  margin: 4px 0 4px 8px;

  &.show {
    grid-template-rows: 1fr;
  }
}

.nav-submenu-inner {
  min-height: 0;
  overflow: hidden;
}

.nav-submenu-content {
  padding: 4px 0;
}

/* Collapsed 状态 */
.sidebar.collapsed {
  .sidebar-header {
    justify-content: center;
    padding: 16px 12px;
  }

  .collapse-btn {
    display: none;
  }

  .nav-item {
    justify-content: center;
    padding: 12px;

    .nav-icon {
      margin-right: 0;
      font-size: 20px;
    }
  }
}

/* ========== 主内容区 ========== */
.main-area {
  flex: 1;
  margin-left: @sidebar-width;
  min-height: 100vh;
  height: 100vh;
  background: @bg-page;
  transition: margin-left 0.2s ease;
  overflow-y: auto;
  overflow-x: hidden;

  &.sidebar-collapsed {
    margin-left: @sidebar-collapsed-width;
  }

  &.no-sidebar {
    margin-left: 0;
  }
}

/* ========== 响应式 ========== */
@media (max-width: 768px) {
  .layout-wrapper {
    display: block;
  }

  .mobile-topbar {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    z-index: 90;
    display: flex;
    align-items: center;
    gap: 12px;
    height: 56px;
    padding: 0 14px;
    border-bottom: 1px solid @border-color;
    background: fade(@bg-card, 96%);
    backdrop-filter: blur(10px);
  }

  .mobile-menu-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    color: @primary;
    font-size: 20px;
    cursor: pointer;

    &:active {
      background: @bg-page;
    }
  }

  .mobile-logo {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
  }

  .mobile-logo-img {
    display: block;
    width: auto;
    height: 24px;
  }

  .mobile-title {
    min-width: 0;
    flex: 1 1 auto;
    color: @primary;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
    overflow: hidden;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-nav-mask {
    position: fixed;
    inset: 0;
    z-index: 95;
    display: block;
    background: rgba(0, 0, 0, 0.26);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;

    &.show {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .sidebar {
    width: min(82vw, @sidebar-width);
    transform: translateX(-100%);
    z-index: 110;
    box-shadow: none;
    transition:
      width 0.2s ease,
      transform 0.2s ease;

    &.collapsed,
    &.mobile-open {
      transform: translateX(0);
      width: min(82vw, @sidebar-width);
    }
  }

  .sidebar-header {
    padding: 14px;
    min-height: 56px;
  }

  .desktop-collapse-btn {
    display: none;
  }

  .collapse-btn.mobile-close-btn {
    display: inline-flex;
    flex: 0 0 auto;
  }

  .sidebar.collapsed {
    .sidebar-header {
      justify-content: space-between;
      padding: 14px;
    }

    .collapse-btn.mobile-close-btn {
      display: inline-flex;
    }

    .nav-item {
      justify-content: flex-start;
      padding: 12px 16px;
    }
  }

  .nav-text,
  .nav-arrow {
    display: inline-flex;
  }

  .nav-submenu {
    display: block;
  }

  .main-area {
    margin-left: 0;

    &.sidebar-collapsed {
      margin-left: 0;
    }

    &.has-mobile-nav {
      padding-top: 56px;
    }
  }
}
</style>
